/**
 * Inline SVG icon set.
 *
 * Purani site font-awesome + flaticon + stroke-gap-icons (teen icon fonts, ~500KB
 * of font files) load karti thi. Yahan sirf jo icons chahiye wahi hain, seedha
 * markup me — na koi extra request, na icon font ka FOUT.
 *
 * Sab 24x24 grid par, `currentColor` se rangte hain, to CSS se colour control hota hai.
 */

type IconProps = {
  size?: number
  className?: string
}

function base(size: number, className?: string) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  }
}

/* ------------------------------- UI / arrows ------------------------------- */

export const ArrowRight = ({ size = 16, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

export const ChevronDown = ({ size = 14, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
)

export const ChevronRight = ({ size = 14, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="m9 18 6-6-6-6" />
  </svg>
)

export const Menu = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
)

export const Close = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)

/* --------------------------------- contact -------------------------------- */

export const Phone = ({ size = 16, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M15.5 21A12.5 12.5 0 0 1 3 8.5 2.5 2.5 0 0 1 5.5 6h1.8a1 1 0 0 1 .96.73l.7 2.4a1 1 0 0 1-.28 1L7.4 11.4a11 11 0 0 0 5.2 5.2l1.27-1.28a1 1 0 0 1 1-.27l2.4.7a1 1 0 0 1 .73.96v1.8A2.5 2.5 0 0 1 15.5 21Z" />
  </svg>
)

export const Mail = ({ size = 16, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="2.5" y="5" width="19" height="14" rx="2" />
    <path d="m3 6.5 9 6 9-6" />
  </svg>
)

export const MapPin = ({ size = 16, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M20 10.5c0 5.2-8 12-8 12s-8-6.8-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10.5" r="2.8" />
  </svg>
)

export const Calendar = ({ size = 16, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="3.5" y="5" width="17" height="16" rx="2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
)

/* --------------------------------- socials -------------------------------- */

export const LinkedIn = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M6.94 8.5H3.9V21h3.04V8.5ZM5.42 3a1.77 1.77 0 1 0 0 3.53 1.77 1.77 0 0 0 0-3.53ZM21 13.85c0-3.5-1.87-5.13-4.36-5.13-2.01 0-2.91 1.11-3.41 1.88V8.5h-3.05c.04.86 0 12.5 0 12.5h3.05v-6.98c0-.27.02-.55.1-.75.22-.55.72-1.12 1.57-1.12 1.11 0 1.55.85 1.55 2.08V21H21v-7.15Z" />
  </svg>
)

export const Facebook = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M14.5 8.5V6.9c0-.76.17-1.15 1.35-1.15h1.5V3.1c-.26-.03-1.15-.1-2.19-.1-2.17 0-3.66 1.32-3.66 3.76V8.5H9v2.9h2.5V21h3V11.4h2.53l.37-2.9h-2.9Z" />
  </svg>
)

export const Instagram = ({ size = 16, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="4.6" />
    <circle cx="12" cy="12" r="3.9" />
    <circle cx="17" cy="7" r="1" fill="currentColor" stroke="none" />
  </svg>
)

/* ------------------------------ value props -------------------------------- */

export const ShieldCheck = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M12 22s8-3.4 8-9.4V5.9l-8-3-8 3v6.7C4 18.6 12 22 12 22Z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </svg>
)

export const Scales = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M12 3.5v17M7 20.5h10M12 6.5 4.5 8M12 6.5 19.5 8" />
    <path d="M4.5 8 2 14a2.6 2.6 0 0 0 5 0L4.5 8ZM19.5 8 17 14a2.6 2.6 0 0 0 5 0L19.5 8Z" />
  </svg>
)

export const Users = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
    <path d="M16.5 5.2a3.2 3.2 0 0 1 0 5.9M17.8 14.4A6.2 6.2 0 0 1 21.5 20" />
  </svg>
)

/*
 * "Integrity & Trust" ke liye seal + check.
 * Pehle yahan handshake tha, par 22px par uske overlapping curves ek squiggle
 * jaise dikh rahe the — is size par simple shape hi padhne me aata hai.
 */
export const BadgeCheck = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M12 2.6l2.1 1.7 2.7-.2.8 2.6 2.4 1.3-.8 2.6.8 2.6-2.4 1.3-.8 2.6-2.7-.2L12 21.4l-2.1-1.7-2.7.2-.8-2.6-2.4-1.3.8-2.6-.8-2.6 2.4-1.3.8-2.6 2.7.2L12 2.6Z" />
    <path d="m9.2 12.1 2 2 3.6-3.9" />
  </svg>
)

/* ----------------------------- practice areas ------------------------------ */

export const Gavel = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="m13.5 7.5-6 6M10.5 4.5 16.5 10.5M15.5 9.5 21 15M5 21h9" />
    <rect x="8.6" y="2.6" width="8" height="4" rx="1" transform="rotate(45 12.6 4.6)" />
    <rect x="13.6" y="12.6" width="8" height="4" rx="1" transform="rotate(45 17.6 14.6)" />
  </svg>
)

export const Building = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M4 21h16M6 21V6.5L12 3l6 3.5V21" />
    <path d="M9.5 21v-4.5h5V21M9.5 9h1.5M13 9h1.5M9.5 12.5H11M13 12.5h1.5" />
  </svg>
)

export const Landmark = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M3.5 20.5h17M3.5 9.5h17L12 4 3.5 9.5ZM6.5 9.5v11M10.5 9.5v11M13.5 9.5v11M17.5 9.5v11" />
  </svg>
)

export const Briefcase = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="2.8" y="7" width="18.4" height="13" rx="2" />
    <path d="M8.5 7V5.4a1.6 1.6 0 0 1 1.6-1.6h3.8a1.6 1.6 0 0 1 1.6 1.6V7M2.8 12.5h18.4" />
  </svg>
)

export const Banknote = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M6 9.5v5M18 9.5v5" />
  </svg>
)

export const FileText = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </svg>
)

export const Home = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1v-9.5Z" />
  </svg>
)

export const Lightbulb = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M9.5 18h5M10 21h4" />
    <path d="M12 3a6 6 0 0 0-3.6 10.8c.5.4.8 1 .9 1.6l.1.6h5.2l.1-.6c.1-.6.4-1.2.9-1.6A6 6 0 0 0 12 3Z" />
  </svg>
)

export const Award = ({ size = 26, className }: IconProps) => (
  <svg {...base(size, className)}>
    <circle cx="12" cy="9" r="5.5" />
    <path d="m8.5 13.8-1 7.2 4.5-2.5 4.5 2.5-1-7.2" />
  </svg>
)

/* -------------------------------- feedback --------------------------------- */

export const CheckCircle = ({ size = 28, className }: IconProps) => (
  <svg {...base(size, className)}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.2 12.2 2.6 2.6 5-5.2" />
  </svg>
)

export const AlertCircle = ({ size = 28, className }: IconProps) => (
  <svg {...base(size, className)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.2M12 16.3v.2" />
  </svg>
)

export const Upload = ({ size = 16, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M12 15.5V4M8 7.5 12 3.5l4 4M4 15v3.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V15" />
  </svg>
)

export const Quote = ({ size = 26, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M9.2 6C6.3 7.4 4.6 9.9 4.6 13v5h6v-6H7.4c.1-1.7 1-3 2.8-4L9.2 6Zm9.6 0c-2.9 1.4-4.6 3.9-4.6 7v5h6v-6H17c.1-1.7 1-3 2.8-4L18.8 6Z" />
  </svg>
)

/* ---------------------------------------------------------------------------
   Practice area ka naam DB se aata hai (Website_LexPractice), isliye icon
   keyword se chunte hain — nayi practice area add hone par bhi kuch na kuch
   sensible icon mil jaata hai (default: Scales).
   ------------------------------------------------------------------------- */

/*
 * Order maayne rakhta hai — pehla match jeetta hai, isliye zyada khaas pattern
 * upar hone chahiye. Jaise "INTELLECTUAL PROPERTY RIGHT DISPUTES" me "property"
 * bhi aata hai; agar property ka rule pehle hota to use ghar ka icon mil jaata.
 */
const PRACTICE_ICONS: Array<[RegExp, (p: IconProps) => React.ReactElement]> = [
  [/intellectual|ipr|trademark|patent|copyright/i, Lightbulb],
  [/real\s*estate|propert|land\b/i, Home],
  [/criminal/i, Gavel],
  [/corporate|company/i, Building],
  [/bank|financ|insolven|debt/i, Banknote],
  [/family|matrimon|divorce/i, Users],
  [/commercial|contract|agreement/i, FileText],
  [/service|tribunal|labour|employ/i, Briefcase],
  [/constitut|writ|civil/i, Landmark],
  [/tax|gst/i, Award],
]

export function practiceIcon(name: string) {
  const hit = PRACTICE_ICONS.find(([re]) => re.test(name))
  return hit ? hit[1] : Scales
}
